import type { PrescriptionLayoutResponse } from '@doccareplus/ui/prescription';
import { BaseApiService } from '@doccareplus/api-client';

// HTTP adapter for the anonymous guest document-share endpoint (api/public/shared-documents/*). No auth: the
// opaque token in the URL is the only credential. Types mirror the C# Contracts.DocumentShares records 1:1
// (camelCase JSON). DocumentType is the enum NAME string, matching how the API exposes enums elsewhere.

export type SharedDocumentType = 'Invoice' | 'Prescription' | 'Receipt';

export interface SharedInvoiceLine {
  description: string;
  quantity: number;
  unitPrice: number;
  lineDiscountType: string | null;
  lineDiscountValue: number | null;
  grossAmount: number;
  discountAmount: number;
  netAmount: number;
}

export interface SharedInvoice {
  invoiceNumber: string;
  invoiceDate: string;
  patientDisplayName: string;
  clinicDisplayName: string;
  currency: string;
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  total: number;
  paymentInstructions: string | null;
  lines: SharedInvoiceLine[];
}

export interface SharedReceipt {
  receiptNumber: string;
  paymentDate: string;
  amount: number;
  method: string;
  invoiceNumber: string;
  patientDisplayName: string;
  clinicDisplayName: string;
  currency: string;
  invoiceTotal: number;
  balanceAfterPayment: number;
}

export interface SharedPrescriptionItem {
  medicineName: string;
  dose: string | null;
  frequency: string | null;
  duration: string | null;
  instructions: string | null;
  order: number;
}

export interface SharedPrescription {
  patientDisplayName: string;
  clinicDisplayName: string;
  providerDisplayName: string;
  signedByDisplayName: string | null;
  visitDate: string;
  finalizedAt: string;
  diagnosis: string | null;
  patientAdvice: string | null;
  followUpDate: string | null;
  items: SharedPrescriptionItem[];
  investigations: string[];
  /** The doctor half of the letterhead, frozen at signing. Absent when never recorded. */
  providerQualification?: string | null;
  providerRegistrationNumber?: string | null;
  patientNumber?: string | null;
  patientAge?: number | null;
  patientGender?: string | null;
  chiefComplaint?: string | null;
  allergySummary?: string | null;
  vitals?: {
    weight?: number | null;
    weightUnit?: string | null;
    systolic?: number | null;
    diastolic?: number | null;
    temperature?: number | null;
    temperatureUnit?: string | null;
    pulse?: number | null;
  } | null;
}

export interface OpenSharedDocumentResult {
  documentType: SharedDocumentType;
  invoice: SharedInvoice | null;
  receipt: SharedReceipt | null;
  prescription: SharedPrescription | null;
  /**
   * Present only for prescriptions: the issuing clinic's live letterhead and effective prescription format, so a
   * guest's copy matches the clinic's own print view. Visibility toggles are applied server-side.
   */
  prescriptionLayout?: PrescriptionLayoutResponse | null;
}

class SharedDocumentsApiService extends BaseApiService {
  constructor() {
    super('/public/shared-documents');
  }

  // POST so a browser link-preview never counts as an access; the guest SPA calls it once on mount.
  open(token: string): Promise<OpenSharedDocumentResult> {
    return this.post<OpenSharedDocumentResult>(`/${encodeURIComponent(token)}/open`);
  }
}

export const sharedDocumentsApi = new SharedDocumentsApiService();
